/**
 * Reselect-based selectors for accessing run set state with optimal re-render
 * behavior. These replace the usePart hooks with a standard,
 * well-documented approach that follows Redux best practices.
 *
 * ## How to Use
 *
 * Use these selectors with `useMemoizedSelector` from `state/hooks.ts`:
 *
 *   const project = useMemoizedSelector(makeRunSetProjectSelector, runSetRef);
 *
 * `useMemoizedSelector` memoizes the selector factory based on its arguments,
 * so the selector is only recreated when `runSetRef` changes.
 *
 * ## When to Add a New Selector Here
 *
 * Add a selector to this file when:
 * - Multiple components need the same derived data from run sets
 * - You're extracting a single property or small group of related properties
 * - The selector follows the existing patterns (property, nested ref, etc.)
 *
 * Keep composite/query-specific selectors colocated with their usage instead.
 * For example, `makeRunSetCountQueryPropsSelector` lives in `selectedCountQuery.ts`
 * because it's only used there.
 *
 * @see https://reselect.js.org/api/createSelector
 * @see state/hooks.ts for useMemoizedSelector implementation
 */
import {createSelector, type Selector} from 'reselect';

import {
  type Filter as FilterType,
  type Ref as FilterRef,
} from '../../state/views/filter/types';
import {
  type GroupSelections,
  type Ref as GroupSelectionsRef,
} from '../../state/views/groupSelections/types';
import {
  type Ref as RunSetRef,
  type RunSetPart,
} from '../../state/views/runSet/types';
import {type Ref as SortRef, type Sort} from '../../state/views/sort/types';
import {type RootState} from '../../types/redux';
import {EMPTY_ALL_SELECTIONS} from '../../util/selectionmanager';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';

// =============================================================================
// Base Selectors
// =============================================================================
// These are the foundation for all other selectors. They look up a normalized
// part by its ref. Other selectors should build on these rather than accessing
// state.views.parts directly.

/**
 * Base selector: get a run set from normalized state by ref.
 * This is the foundation for all run set property selectors.
 */
export const makeRunSetSelector =
  (runSetRef: RunSetRef) => (state: RootState) =>
    state.views.parts[runSetRef.type][runSetRef.id];

/**
 * Base selector: get group selections from normalized state by ref.
 */
export const makeGroupSelectionsSelector =
  (groupSelectionsRef: GroupSelectionsRef) => (state: RootState) =>
    state.views.parts[groupSelectionsRef.type][groupSelectionsRef.id];

// =============================================================================
// Run Set Property Selectors
// =============================================================================
// Use these to extract individual properties from a run set. Each uses
// createSelector to memoize the result, so the component only re-renders
// when that specific property changes.

/**
 * Selector factory: get project from run set.
 */
export const makeRunSetProjectSelector = (
  runSetRef: RunSetRef
): Selector<RootState, RunSetPart['project']> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => runSet.project);

/**
 * Selector factory: get groupSelectionsRef from run set.
 */
export const makeRunSetGroupSelectionsRefSelector = (
  runSetRef: RunSetRef
): Selector<RootState, GroupSelectionsRef> =>
  createSelector(
    makeRunSetSelector(runSetRef),
    runSet => runSet.groupSelectionsRef
  );

/**
 * Selector factory: get filtersRef from run set.
 */
export const makeRunSetFiltersRefSelector = (
  runSetRef: RunSetRef
): Selector<RootState, FilterRef> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => runSet.filtersRef);

/**
 * Selector factory: get sortRef from run set.
 */
export const makeRunSetSortRefSelector = (
  runSetRef: RunSetRef
): Selector<RootState, SortRef> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => runSet.sortRef);

/**
 * Selector factory: get pinned run IDs and baseline run ID from run set.
 */
export const makeRunSetPinningSelector = (
  runSetRef: RunSetRef
): Selector<RootState, {pinnedRunIds: string[]; baselineRunId?: string}> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    pinnedRunIds: runSet.pinnedRunIds ?? [],
    baselineRunId: runSet.baselineRunId,
  }));

/**
 * Selector factory: get only pinnedRunIds from run set.
 */
export const makeRunSetPinnedRunIdsSelector = (
  runSetRef: RunSetRef
): Selector<RootState, string[]> =>
  createSelector(
    makeRunSetSelector(runSetRef),
    runSet => runSet.pinnedRunIds ?? []
  );

/**
 * Selector factory: get only baselineRunId from run set.
 */
export const makeRunSetBaselineRunIdSelector = (
  runSetRef: RunSetRef
): Selector<RootState, string | undefined> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => runSet.baselineRunId);

// =============================================================================
// Nested Ref Selectors
// =============================================================================
// Example: runSet.filtersRef points to the filters object.
//
// Why no createSelector here?
// createSelector memoizes based on input selector results. But the nested ref
// (e.g., filtersRef) is read inside the selector, not passed as an input. If we
// used createSelector, it would only see the runSet changing, not the filtersRef
// or the filters object it points to. By using a plain function, we ensure fresh
// lookups on every call. This is fine because object lookups are O(1).
//
// When the child part changes, useSelector's shallowEqual comparison will detect
// the new reference and trigger a re-render.

/**
 * Selector factory: get sort from run set.
 * Follows nested ref pattern: runSet -> sortRef -> sort
 */
export const makeRunSetSortSelector = (
  runSetRef: RunSetRef
): Selector<RootState, Sort> => {
  const selectSortRef = makeRunSetSortRefSelector(runSetRef);

  return (state: RootState) => {
    const sortRef = selectSortRef(state);
    return state.views.parts[sortRef.type][sortRef.id];
  };
};

/**
 * Selector factory: get filters from run set.
 * Follows nested ref pattern: runSet -> filtersRef -> filters
 */
export const makeRunSetFiltersSelector = (
  runSetRef: RunSetRef
): Selector<RootState, FilterType> => {
  const selectFiltersRef = makeRunSetFiltersRefSelector(runSetRef);

  return (state: RootState) => {
    const filtersRef = selectFiltersRef(state);
    return state.views.parts[filtersRef.type][filtersRef.id];
  };
};

/**
 * Selector factory: get group selections from run set.
 * Follows nested ref pattern: runSet -> groupSelectionsRef -> groupSelections
 */
export const makeRunSetGroupSelectionsSelector = (
  runSetRef: RunSetRef
): Selector<RootState, GroupSelections> => {
  const selectGroupSelectionsRef =
    makeRunSetGroupSelectionsRefSelector(runSetRef);

  return (state: RootState) => {
    const groupSelectionsRef = selectGroupSelectionsRef(state);
    return state.views.parts[groupSelectionsRef.type][groupSelectionsRef.id];
  };
};

/**
 * Selector factory: get grouping from run set.
 * Follows nested ref pattern: runSet -> groupSelectionsRef -> groupSelections -> grouping
 */
export const makeRunSetGroupingSelector = (
  runSetRef: RunSetRef
): Selector<RootState, GroupSelections['grouping']> => {
  const selectGroupSelections = makeRunSetGroupSelectionsSelector(runSetRef);

  return (state: RootState) => {
    const groupSelections = selectGroupSelections(state);
    return groupSelections.grouping;
  };
};

/**
 * Selector factory: get group selections with single-run mode applied.
 * When singleRunName is provided, overrides grouping to [] and selections
 * to EMPTY_ALL_SELECTIONS while preserving expandedRowAddresses from store.
 *
 * Use this instead of makeRunSetGroupSelectionsSelector when the consumer
 * needs to respect single-run mode.
 */
export const makeSingleRunGroupSelectionsSelector = (
  runSetRef: RunSetRef
): Selector<RootState, GroupSelections> =>
  createSelector(makeRunSetGroupSelectionsSelector(runSetRef), gs => ({
    grouping: [] as GroupSelections['grouping'],
    selections: EMPTY_ALL_SELECTIONS,
    expandedRowAddresses: gs.expandedRowAddresses,
  }));

/**
 * Selector factory: get runNameTruncationType from run set.
 */
export const makeRunSetTruncationTypeSelector = (
  runSetRef: RunSetRef
): Selector<RootState, TruncationType> =>
  createSelector(
    makeRunSetSelector(runSetRef),
    runSet => runSet.runNameTruncationType ?? TruncationType.Middle
  );
